import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner, Badge, Modal, Row, Col } from 'react-bootstrap';
import { Search, AlertCircle, Info, CheckCircle2, XCircle } from 'lucide-react';
import useAuthHeaders from '../../hooks/useAuthHeaders';
import RequestViewer from './RequestViewer';

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

const RepseTab = () => {
  const { getToken, isSignedIn } = useAuthHeaders();

  const [razonSocial, setRazonSocial] = useState('');
  const [loading, setLoading]         = useState(false);
  const [resultado, setResultado]     = useState(null);
  const [errorMsg, setErrorMsg]       = useState('');
  const [showError, setShowError]     = useState(false);
  const [lastRequest, setLastRequest] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const term = razonSocial.trim();
    if (term.length < 3) return;

    setLoading(true);
    setResultado(null);

    try {
      const token  = await getToken();
      const params = new URLSearchParams({ razonSocial: term });
      const res    = await fetch(`${API_URL_V2}/Repse/consultar/razon-social-directo?${params}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text() || `Error HTTP ${res.status}`);
      const data = await res.json();

      setResultado(data);
      setLastRequest({
        method:      'GET',
        baseUrl:     `${API_URL_V2}/Repse/consultar/razon-social-directo`,
        queryParams: params.toString(),
        headers:     {},
        formFields:  null,
        jsonBody:    null,
        jsPrefix:    null,
      });
    } catch (err) {
      setErrorMsg(err.message || 'Error al consultar REPSE.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = isSignedIn && !loading && razonSocial.trim().length >= 3;

  return (
    <>
      <Card className="tab-section-card border-0 shadow-sm">
        <Card.Body>

          <Alert variant="info" className="d-flex align-items-start gap-2 mb-4">
            <Info size={18} className="flex-shrink-0 mt-1" />
            <div>
              <strong>Consulta REPSE — Padrón Público de Servicios Especializados (STPS)</strong>
              <div className="mt-1 small">
                Busca empresas en el Registro Público REPSE por razón social. La consulta es
                directa e inmediata: el resultado se muestra en la misma respuesta, sin necesidad
                de esperar ni volver a consultar.
              </div>
            </div>
          </Alert>

          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={8}>
                <Form.Label className="form-label-modern">Razón Social</Form.Label>
                <Form.Control
                  className="form-control-modern"
                  type="text"
                  placeholder="Ej. SERVICIOS INDUSTRIALES DEL NORTE"
                  value={razonSocial}
                  onChange={(e) => setRazonSocial(e.target.value)}
                />
                <Form.Text className="text-muted">Mínimo 3 caracteres.</Form.Text>
              </Col>
            </Row>

            {!isSignedIn && (
              <Alert variant="warning" className="d-flex align-items-center py-2 mb-3">
                <AlertCircle size={16} className="me-2 flex-shrink-0" />
                <small>Debes iniciar sesión para usar esta consulta.</small>
              </Alert>
            )}

            <Button type="submit" className="btn-gradient" disabled={!canSubmit}>
              {loading
                ? <><Spinner animation="border" size="sm" className="me-2" />Consultando…</>
                : <><Search size={18} className="me-2" />Buscar en REPSE</>
              }
            </Button>
          </Form>

          {/* ── Resultado de la consulta ─── */}
          {resultado && (
            <div className="mt-4 p-3" style={{
              background: 'linear-gradient(135deg,rgba(19,160,255,.05),rgba(0,212,170,.05))',
              borderRadius: 12,
              border: '1px solid rgba(19,160,255,.12)',
            }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                {resultado.success ? (
                  <>
                    <CheckCircle2 size={18} color="#00d4aa" />
                    <Badge bg="success">
                      {resultado.registros?.length > 1 ? 'Múltiples registros encontrados' : 'Registro encontrado'}
                    </Badge>
                  </>
                ) : (
                  <>
                    <XCircle size={18} color="#ff6b6b" />
                    <Badge bg="danger">Sin resultado</Badge>
                  </>
                )}
              </div>

              {resultado.success ? (
                <div className="mt-3 d-flex flex-column gap-3">
                  {resultado.registros.map((registro) => (
                    <RegistroCard key={registro.candidatoId} registro={registro} />
                  ))}
                </div>
              ) : (
                <Alert variant="warning" className="mt-3 mb-0 py-2 d-flex align-items-center gap-2">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  <span className="small">
                    {resultado.mensaje || 'No se encontró ningún registro para la razón social indicada.'}
                  </span>
                </Alert>
              )}
            </div>
          )}

          <RequestViewer request={lastRequest} />

        </Card.Body>
      </Card>

      <Modal show={showError} onHide={() => setShowError(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '3px solid #ff6b6b' }}>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#1a1f3a' }}>
            <AlertCircle size={20} color="#ff6b6b" />
            Error al consultar REPSE
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0" style={{ color: '#4a5568', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {errorMsg}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const RegistroCard = ({ registro }) => {
  const data = registro.data || {};
  return (
    <div style={{
      background: '#fff',
      borderRadius: 10,
      padding: '1rem',
      border: '1px solid rgba(0,212,170,.25)',
    }}>
      <div className="d-flex align-items-center gap-2 mb-3">
        <CheckCircle2 size={18} color="#00d4aa" />
        <strong style={{ color: '#1a1f3a' }}>Registro REPSE</strong>
        {data.fechaVencimientoRegistro && (
          <Badge bg="success" className="ms-auto">
            Vigente hasta {new Date(data.fechaVencimientoRegistro).toLocaleDateString('es-MX')}
          </Badge>
        )}
      </div>
      <Row className="g-2 small">
        <Col xs={6} md={3}>
          <span className="text-muted d-block">Folio</span>
          <strong>{data.folio || '—'}</strong>
        </Col>
        <Col xs={12} md={9}>
          <span className="text-muted d-block">Razón Social</span>
          <strong>{data.razonSocial || '—'}</strong>
        </Col>
        <Col xs={6} md={3}>
          <span className="text-muted d-block">RFC</span>
          {data.rfc || '—'}
        </Col>
        <Col xs={6} md={3}>
          <span className="text-muted d-block">Entidad</span>
          {data.entidad || '—'}
        </Col>
        <Col xs={6} md={3}>
          <span className="text-muted d-block">Municipio</span>
          {data.municipio || '—'}
        </Col>
        <Col xs={6} md={3}>
          <span className="text-muted d-block">Fecha de registro</span>
          {data.fechaRegistro
            ? new Date(data.fechaRegistro).toLocaleDateString('es-MX')
            : '—'}
        </Col>
        {registro.actividades?.length > 0 && (
          <Col xs={12}>
            <span className="text-muted d-block mb-1">Actividades</span>
            <div className="d-flex flex-wrap gap-1">
              {registro.actividades.map((a, i) => (
                <Badge key={a.folio || i} bg="light" text="dark"
                  style={{ border: '1px solid #dee2e6', fontWeight: 400 }}>
                  {a.descripcion}
                </Badge>
              ))}
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default RepseTab;
